import axios from 'axios'
import { Notice } from 'view-design'
import router from '@/router'
import { showLoading, closeLoading } from '@/utils/loading'
import { resetTokenAndClearUser } from '@/utils'

// 创建 axios 实例
const service = axios.create({
    baseURL: window.location.origin,
    timeout: 60000,
});

// 刷新 Token 相关状态
let isRefreshing = false;          // 是否正在刷新
let failedQueue = [];              // 等待队列

// 处理队列中的请求
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

// 刷新 Token 的方法
const refreshToken = () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
        return Promise.reject(new Error('No refresh token'));
    }

    // 注意：使用全新的 axios 实例，避免拦截器干扰
    return axios.create({ baseURL: window.location.origin })({
        url: '/api/Company/refresh',
        method: 'POST',
        data: { refreshToken },
        headers: { 'Content-Type': 'application/json' },
        // 标记该请求为刷新请求，避免响应拦截器再次处理
        _isRefreshRequest: true
    }).then(res => {
        if (res.data && res.data.state === 1) {
            const newTokens = res.data.data;
			let authToken  = 'Bearer ' + newTokens.item1
            localStorage.setItem('token', authToken);
            if (newTokens.item2) {
                localStorage.setItem('refreshToken', newTokens.item2);
            }
            return authToken;
        } else {
            throw new Error('Refresh failed');
        }
    }).catch(err => {
        // 刷新失败，清除本地 Token 并跳转登录
        resetTokenAndClearUser(); // 此函数应清除 token 和用户信息
        router.push('/login');
        return Promise.reject(err);
    });
};

// 请求拦截器
service.interceptors.request.use(config => {
    showLoading();
    if (localStorage.getItem('token')) {
        config.headers.Authorization = localStorage.getItem('token');
    }
    if (localStorage.getItem('schema')) {
        config.headers.Schema = localStorage.getItem('schema');
    }
    if (localStorage.getItem('workshop')) {
        config.headers.WorkShop = localStorage.getItem('workshop');
    }
    return config;
}, (error) => Promise.reject(error));

// 响应拦截器
service.interceptors.response.use(response => {
    closeLoading();
    return response.data;
}, async (error) => {
    closeLoading();

    const originalRequest = error.config;

    // 如果错误不是 401 或没有响应对象，直接显示错误并拒绝
    if (!error.response || error.response.status !== 401) {
        Notice.error({
            title: '错误提示',
            desc:  error.message || '请求失败' //|| error.response?.data?.errorMsg
        });
        return Promise.reject(error);
    }

    // 如果是刷新请求本身失败，直接拒绝（避免死循环）
    if (originalRequest._isRefreshRequest) {
        return Promise.reject(error);
    }

    // 如果没有 refreshToken，直接跳转登录
    if (!localStorage.getItem('refreshToken')) {
        resetTokenAndClearUser();
        router.push('/login');
        return Promise.reject(error);
    }

    // 如果正在刷新，将当前请求加入队列
    if (isRefreshing) {
        return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
        }).then(token => {
            originalRequest.headers.Authorization = token;
            return service(originalRequest); // 重试原请求
        }).catch(err => {
            return Promise.reject(err);
        });
    }

    // 开始刷新 Token
    isRefreshing = true;

    try {
        const newToken = await refreshToken();
        // 处理队列中的请求
        processQueue(null, newToken);

        // 重试当前请求
        originalRequest.headers.Authorization = newToken;
        return service(originalRequest);
    } catch (refreshError) {
        // 刷新失败，队列中的请求也一并拒绝
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
    } finally {
        isRefreshing = false;
    }
});

export default service;